// Shared brand-recognition registry used by both DetectedModelsPanel
// (clusters detection) and DataSurfaceV3 (per-row chip via SQL CASE).
//
// Each rule maps a structural-fingerprint signature to a friendly
// service label + emoji. Order matters: first match wins. Add new
// services by appending. Path-syntax is dot-separated leaf paths
// inside the event payload.

export interface BrandRule {
  /** Stable id used in SQL CASE return value + as React key */
  id: string;
  emoji: string;
  label: string;
  /** All paths must be present in the event's payload for a match. */
  signature: string[];
}

export const BRAND_RULES: BrandRule[] = [
  { id: 'slack',     emoji: '💬', label: 'Slack',     signature: ['event.channel', 'team_id'] },
  { id: 'slack',     emoji: '💬', label: 'Slack',     signature: ['event.user', 'team_id'] },
  { id: 'slack',     emoji: '💬', label: 'Slack',     signature: ['event.text', 'event_id'] },
  { id: 'zoom',      emoji: '🎥', label: 'Zoom',      signature: ['payload.object.host_email'] },
  { id: 'zoom',      emoji: '🎥', label: 'Zoom',      signature: ['payload.account_id', 'event'] },
  { id: 'mailchimp', emoji: '📧', label: 'Mailchimp', signature: ['data.list_id', 'data.email'] },
  { id: 'mailchimp', emoji: '📧', label: 'Mailchimp', signature: ['data.list_id', 'data.merges'] },
  { id: 'notion',    emoji: '📋', label: 'Notion',    signature: ['created_by.object', 'last_edited_by.id'] },
  { id: 'notion',    emoji: '📋', label: 'Notion',    signature: ['parent.database_id'] },
  { id: 'zendesk',   emoji: '🎫', label: 'Zendesk',   signature: ['detail.brand_id', 'detail.ticket_id'] },
  { id: 'zendesk',   emoji: '🎫', label: 'Zendesk',   signature: ['detail.assignee_id', 'detail.form_id'] },
  { id: 'calendly',  emoji: '📅', label: 'Calendly',  signature: ['payload.invitee.email', 'payload.cancel_url'] },
  { id: 'calendly',  emoji: '📅', label: 'Calendly',  signature: ['payload.scheduled_event'] },
  { id: 'hubspot',   emoji: '🟠', label: 'HubSpot',   signature: ['subscriptionType', 'objectId'] },
  { id: 'stripe',    emoji: '💳', label: 'Stripe',    signature: ['data.object.id', 'livemode'] },
  { id: 'stripe',    emoji: '💳', label: 'Stripe',    signature: ['data.object', 'request', 'type'] },
  { id: 'linear',    emoji: '📐', label: 'Linear',    signature: ['data.identifier', 'data.team'] },
  { id: 'linear',    emoji: '📐', label: 'Linear',    signature: ['data.priorityLabel', 'data.state'] },
  { id: 'intercom',  emoji: '💬', label: 'Intercom',  signature: ['data.item.email', 'topic'] },
  { id: 'quickbooks', emoji: '🟢', label: 'QuickBooks', signature: ['eventNotifications'] },
  { id: 'twilio',    emoji: '📞', label: 'Twilio',    signature: ['CallSid', 'From'] },
  { id: 'shopify',   emoji: '🛒', label: 'Shopify',   signature: ['admin_graphql_api_id'] },
  { id: 'segment',   emoji: '📊', label: 'Segment',   signature: ['anonymousId', 'context.library.name'] },
  { id: 'trello',    emoji: '🃏', label: 'Trello',    signature: ['action.type', 'action.data.board.id'] },
  { id: 'github',    emoji: '🐙', label: 'GitHub',    signature: ['repository.full_name', 'sender.login'] },
  { id: 'gitlab',    emoji: '🦊', label: 'GitLab',    signature: ['object_kind', 'project.web_url'] },
];

/** Map of `id` → first matching rule (covers emoji + label + a
 *  representative signature). Used to render a chip when SQL has
 *  classified a row to that id. */
export const BRAND_BY_ID: Record<string, { emoji: string; label: string; signature: string[] }> = (() => {
  const out: Record<string, { emoji: string; label: string; signature: string[] }> = {};
  for (const r of BRAND_RULES) {
    if (!out[r.id]) out[r.id] = { emoji: r.emoji, label: r.label, signature: r.signature };
  }
  return out;
})();

/** Recognise a brand from a path-set (used by DetectedModelsPanel
 *  on the fingerprint groups returned by /views:auto-suggest). */
export function recogniseBrand(paths: string[]): { id: string; emoji: string; label: string } | null {
  const set = new Set(paths);
  for (const rule of BRAND_RULES) {
    if (rule.signature.every(p => set.has(p))) {
      return { id: rule.id, emoji: rule.emoji, label: rule.label };
    }
  }
  return null;
}

/** Build a SQL CASE expression that classifies each event's brand
 *  from its raw payload. Returns the rule id as a string (matches
 *  BRAND_BY_ID keys) or NULL when no rule matches. The expression
 *  references `payload` directly so it must be evaluated in scope
 *  where the base table's `payload` column is available. */
export function buildBrandSqlCase(): string {
  const branches = BRAND_RULES.map(rule => {
    const conditions = rule.signature
      .map(p => `JSON_VALUE(payload, '$.${p}') IS NOT NULL`)
      .join(' AND ');
    return `    WHEN ${conditions} THEN '${rule.id}'`;
  }).join('\n');
  return `CASE\n${branches}\n    ELSE NULL\n  END`;
}

/** AND-joined predicate that filters events to a specific brand id.
 *  Uses the FIRST signature for that brand — sufficient for the
 *  brand-click-to-filter flow. */
export function buildBrandFilterPredicate(brandId: string): string | null {
  const rule = BRAND_BY_ID[brandId];
  if (!rule) return null;
  return rule.signature
    .map(p => `JSON_VALUE(payload, '$.${p}') IS NOT NULL`)
    .join(' AND ');
}
